import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, compressImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PositionPicker } from "@/components/PositionPicker";

const STEPS = ["Compte", "Identité", "Photo", "Position principale", "Positions secondaires"];

export default function Register() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ email: "", password: "", first_name: "", last_name: "", avatar: null, primary_position: "", secondary_positions: [], start_year: "" });
  const YEARS = Array.from({ length: new Date().getFullYear() - 1979 }, (_, i) => new Date().getFullYear() - i);
  const shortName = f.first_name.trim() ? `${f.first_name.trim()}${f.last_name.trim() ? ` ${f.last_name.trim()[0].toUpperCase()}.` : ""}` : "";

  const valid = [
    /\S+@\S+\.\S+/.test(f.email) && f.password.length >= 6,
    f.first_name.trim() && f.last_name.trim() && !!f.start_year,
    true,
    !!f.primary_position,
    true,
  ][step];

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/register", { ...f, start_year: +f.start_year });
      signIn(r.data.token, r.data.user);
      toast.success(`Bienvenue ${r.data.user.short_name} !`);
      nav("/");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : submit());
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) setF({ ...f, avatar: await compressImage(file) });
  };

  return (
    <div className="min-h-[100dvh] mx-auto max-w-md px-6 py-8 flex flex-col" data-testid="register-page">
      <div className="flex items-center gap-3">
        {step > 0 ? <button data-testid="register-back" onClick={() => setStep(step - 1)} className="p-2 -ml-2"><ArrowLeft className="w-5 h-5" /></button>
          : <Link to="/connexion" className="p-2 -ml-2" data-testid="register-to-login"><ArrowLeft className="w-5 h-5" /></Link>}
        <img src="/icon-192.png" alt="Gogoats" className="w-9 h-9 rounded-xl" />
        <div className="flex-1 flex gap-1">
          {STEPS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-cyan-400" : "bg-slate-200"}`} />)}
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-6">Étape {step + 1} / {STEPS.length}</p>
      <h1 className="font-display text-3xl font-extrabold mt-1">{STEPS[step]}</h1>
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="mt-6 flex-1 space-y-3">
          {step === 0 && (
            <>
              <input data-testid="register-email" type="email" placeholder="Email" className="gg-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
              <input data-testid="register-password" type="password" placeholder="Mot de passe (6 caractères min.)" className="gg-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
            </>
          )}
          {step === 1 && (
            <>
              <input data-testid="register-first-name" placeholder="Prénom" className="gg-input" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
              <input data-testid="register-last-name" placeholder="Nom de famille" className="gg-input" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
              {shortName && <p data-testid="register-short-name-preview" className="text-sm text-slate-500">Sur le terrain, vous apparaîtrez comme <b className="text-cyan-600">{shortName}</b></p>}
              <p className="text-xs font-bold text-slate-500 uppercase pt-2">Année de début chez Gogoats</p>
              <select data-testid="register-start-year" className="gg-input" value={f.start_year} onChange={(e) => setF({ ...f, start_year: e.target.value })}>
                <option value="">— Depuis quelle année jouez-vous ? —</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <p className="text-[11px] text-slate-500">Votre ancienneté est calculée automatiquement. Les plus anciens sont prioritaires quand le match est complet.</p>
            </>
          )}
          {step === 2 && (
            <div className="flex flex-col items-center gap-4 pt-4">
              <button data-testid="register-photo-button" onClick={() => fileRef.current.click()} className="w-36 h-36 rounded-full border-2 border-dashed border-cyan-400/60 overflow-hidden flex items-center justify-center bg-white shadow-sm">
                {f.avatar ? <img src={f.avatar} alt="Aperçu" className="w-full h-full object-cover" /> : <Camera className="w-10 h-10 text-cyan-600" />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="register-photo-input" />
              <p className="text-sm text-slate-500 text-center">Photo optionnelle — vous pourrez l'ajouter plus tard.<br />Sans photo, vos initiales seront affichées.</p>
            </div>
          )}
          {step === 3 && <PositionPicker value={f.primary_position} onChange={(v) => setF({ ...f, primary_position: v, secondary_positions: f.secondary_positions.filter((s) => s !== v) })} testid="register-primary" />}
          {step === 4 && (
            <>
              <p className="text-sm text-slate-500 -mt-2">Peut également jouer (plusieurs choix possibles)</p>
              <PositionPicker multi value={f.secondary_positions} exclude={f.primary_position} onChange={(v) => setF({ ...f, secondary_positions: v })} testid="register-secondary" />
            </>
          )}
        </motion.div>
      </AnimatePresence>
      <button data-testid="register-next" disabled={!valid || busy} onClick={next}
        className="mt-6 w-full rounded-2xl bg-cyan-400 text-zinc-950 font-display font-black py-4 active:scale-[0.98] transition disabled:opacity-40">
        {step === 2 && !f.avatar ? "PLUS TARD" : step === STEPS.length - 1 ? (busy ? "CRÉATION…" : "CRÉER MON COMPTE") : "CONTINUER"}
      </button>
    </div>
  );
}
